'use client';

import React, { useEffect, useState } from 'react';
import Image from 'next/image';
import { motion } from 'framer-motion';
import { ArrowRight } from 'lucide-react';
import Link from 'next/link';

function useIsMobile() {
    const [isMobile, setIsMobile] = useState(false);
    useEffect(() => {
        const check = () => setIsMobile(window.innerWidth < 1024);
        check();
        window.addEventListener('resize', check);
        return () => window.removeEventListener('resize', check);
    }, []);
    return isMobile;
}

const services: ServiceItem[] = [
    {
        tag: "Insight-Driven Marketing",
        title: "Performance Marketing",
        description: "Great marketing starts with great decisions. We equip you with the research, insights, frameworks, and strategic oversight needed to unlock meaningful growth.",
        image: "/images/services/performance-marketing/hero.webp",
        video: "/videos/performance-marketing.mp4",
        href: "/services/performance-marketing",
        cta: "Learn More",
    },
    {
        tag: "Branding & Creative Strategy",
        title: "Visual Communication and Strategic Branding",
        description: "We transform brands through visual storytelling and meaningful design. Combining strategic branding with compelling creative, we enable brands to communicate powerfully and authentically.",
        image: "/images/services/brand-strategy/hero.webp",
        href: "/services/brand-strategy",
        cta: "Start Today",
    },
    {
        tag: "Web Design & UX",
        title: "Digital Product Design and Development",
        description: "We craft cutting-edge digital experiences through strategic design thinking and agile development. Our holistic process yields intuitive products that engage users and bring brands to life online.",
        image: "/images/services/website-design/thumbnail.webp",
        video: "/videos/webdesign-services.mp4",
        href: "/services/web-design-ux",
        cta: "Start Today",
    },
    {
        tag: "Search & Visibility",
        title: "SEO: Be Found Everywhere Your Customers Search",
        description: "From organic SEO to paid SEM, local search, data optimization, and AI-powered discovery — we cover every dimension of search visibility to keep your brand at the top.",
        image: "/images/services/seo/hero.webp",
        video: "/videos/search-visibility-service.mp4",
        href: "/services/search-visibility",
        cta: "Learn More",
    },
    {
        tag: "Social Media Marketing",
        title: "Skyrocket your Social Media Presence",
        description: "We strategically amplify brands across social platforms for maximum visibility and engagement. Our data-driven and creative solutions lead to an expansive, relevant social media presence.",
        image: "/images/services/social-media/hero.webp",
        video: "/videos/smm-service.mp4",
        href: "/services/social-media",
        cta: "Start Today",
    },
];

type ServiceItem = {
    tag: string;
    title: string;
    description: string;
    image: string;
    href: string;
    cta: string;
    video?: string;
};

function ServiceRow({ service, index }: { service: ServiceItem; index: number }) {
    const reverse = index % 2 !== 0;
    const isMobile = useIsMobile();

    const TagTitle = (
        <motion.div
            initial={{ opacity: 0, x: isMobile ? 0 : (reverse ? 40 : -40), y: isMobile ? -40 : 0 }}
            whileInView={{ opacity: 1, x: 0, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7, delay: isMobile ? 0 : 0 }}
            className="flex flex-col gap-5"
        >
            <span className="text-xs font-bold uppercase tracking-[0.25em] text-gray-500">{service.tag}</span>
            <Link href={service.href} className="group">
                <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 leading-tight group-hover:text-blue-600 transition-colors">{service.title}</h2>
            </Link>
        </motion.div>
    );

    const ParaButton = (
        <motion.div
            initial={{ opacity: 0, x: isMobile ? 0 : (reverse ? 40 : -40), y: isMobile ? -40 : 0 }}
            whileInView={{ opacity: 1, x: 0, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7, delay: isMobile ? 0.4 : 0 }}
            className="flex flex-col gap-5"
        >
            <p className="text-gray-600 text-base md:text-lg leading-relaxed max-w-lg">{service.description}</p>
            <Link
                href={service.href}
                className="inline-flex items-center gap-3 bg-gray-900 text-white px-6 py-3 rounded-full font-semibold w-fit hover:bg-gray-700 transition-colors mt-2 text-sm md:text-base"
            >
                <span className="w-7 h-7 bg-blue-600 rounded-full flex items-center justify-center shrink-0">
                    <ArrowRight className="w-4 h-4" />
                </span>
                {service.cta}
            </Link>
        </motion.div>
    );

    const Content = (
        <div className="flex flex-col justify-center gap-5">
            {TagTitle}
            {ParaButton}
        </div>
    );

    const ImageBlock = (
        <motion.div
            initial={{ opacity: 0, x: isMobile ? 0 : (reverse ? -40 : 40), y: isMobile ? 40 : 0 }}
            whileInView={{ opacity: 1, x: 0, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7, delay: isMobile ? 0.2 : 0 }}
            className="relative rounded-2xl overflow-hidden bg-gray-100 aspect-[4/3] shadow-xl"
        >
            <Link href={service.href} className="block w-full h-full">
                {service.video ? (
                    <video
                        src={service.video}
                        autoPlay
                        loop
                        muted
                        playsInline
                        className="w-full h-full object-cover" style={{ objectPosition: '50% 30%' }}
                    />
                ) : (
                    <Image src={service.image} alt={service.title} fill className="object-cover hover:scale-105 transition-transform duration-500" sizes="(max-width: 768px) 100vw, 50vw" />
                )}
            </Link>
        </motion.div>
    );

    return (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center py-16 md:py-24 border-b border-gray-100 last:border-0">
            {isMobile ? (
                <>
                    {TagTitle}
                    {ImageBlock}
                    {ParaButton}
                </>
            ) : (
                reverse ? <>{ImageBlock}{Content}</> : <>{Content}{ImageBlock}</>
            )}
        </div>
    );
}

export default function ServicesPage() {
    return (
        <main className="bg-white text-gray-900 overflow-x-hidden">

            {/* Page Header */}
            <div className="pt-32 pb-4 px-6 md:px-16 max-w-7xl mx-auto">
                <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
                    <span className="text-xs font-bold uppercase tracking-[0.3em] text-blue-600 mb-4 block">What We Do</span>
                    <h1 className="text-5xl md:text-7xl font-bold tracking-tight text-gray-900 leading-none">
                        Our <span className="text-blue-600">Services</span>
                    </h1>
                    <p className="mt-4 text-gray-500 text-lg max-w-xl">
                        Five focused disciplines. One mission — grow your brand with precision and purpose.
                    </p>
                </motion.div>
            </div>

            {/* Alternating Service Rows */}
            <div className="px-6 md:px-16 max-w-7xl mx-auto">
                {services.map((service, index) => (
                    <ServiceRow key={index} service={service} index={index} />
                ))}
            </div>

            {/* Bottom CTA */}
            <section className="mt-16 py-24 bg-gray-950 text-center px-6">
                <motion.div
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    className="max-w-2xl mx-auto"
                >
                    <h2 className="text-4xl md:text-5xl font-black text-white mb-6 leading-tight">
                        Ready to scale your brand?
                    </h2>
                    <p className="text-gray-400 text-lg mb-10">
                        Let's build the full growth engine — strategy, design, search, social, and performance.
                    </p>
                    <Link
                        href="/contact"
                        className="inline-flex items-center gap-3 bg-blue-600 text-white px-10 py-5 rounded-full font-bold text-lg hover:bg-blue-700 transition-colors"
                    >
                        Get Started <ArrowRight className="w-5 h-5" />
                    </Link>
                </motion.div>
            </section>
        </main>
    );
}
